/**
 * Authentication Hook (Adapter Pattern)
 * Delegates core auth to @optimgov/module-auth while preserving the existing
 * Zustand-based API surface (AppUser, AuthState, AuthActions, UseAuthReturn).
 * Zero changes needed in the 20+ consuming components.
 *
 * @see D-07, D-08, SPA-03
 */

import { useEffect, useRef } from "react"
import { create } from "zustand"
import { useAuth as useModuleAuth, type AuthUser } from "@optimgov/module-auth"
import { AuthError } from "@/lib/auth-actions"
import { UserRole, User as ReportsUser, Organization, UserPreferences } from "@/types/reports"
import { storeManager } from "@/stores/storeManager"
import { simpleGet, simplePatch } from "@/lib/apiUtils"
import { setAuthBridge } from "@/lib/auth-bridge"
import { useLocaleStore, type Locale } from "@/stores/localeStore"

const AUTH_COPY: Record<Locale, {
	userNotRegistered: string
	refreshError: string
	moduleNotLicensed: string
}> = {
	ca: {
		userNotRegistered: "L'usuari no està registrat al sistema. Contacta amb l'administrador.",
		refreshError: "Error en carregar les dades d'usuari",
		moduleNotLicensed: "Mòdul no llicenciat",
	},
	es: {
		userNotRegistered: "El usuario no está registrado en el sistema. Contacta con el administrador.",
		refreshError: "Error al cargar los datos de usuario",
		moduleNotLicensed: "Módulo no licenciado",
	},
	en: {
		userNotRegistered: "The user is not registered in the system. Contact the administrator.",
		refreshError: "Error loading user data",
		moduleNotLicensed: "Module not licensed",
	},
}

function getAuthCopy() {
	const locale = useLocaleStore.getState().locale
	return AUTH_COPY[locale] ?? AUTH_COPY.ca
}

// Simplified user type for session-based auth
export interface AppUser {
	uid: string
	email: string | null
	displayName: string | null
	emailVerified: boolean
}

// Auth state interface
export interface AuthState {
	user: AppUser | null
	loading: boolean
	error: AuthError | null
	isAuthenticated: boolean
	isEmailVerified: boolean
	initialized: boolean
	// Reports-specific state
	reportsUser: ReportsUser | null
	organization: Organization | null
	permissions: string[]
	// Auth provider info
	authProvider: "azure" | "credentials" | null
}

// Auth actions interface
export interface AuthActions {
	logout: () => Promise<void>
	refreshUser: () => Promise<void>
	clearError: () => void
	checkPermission: (permission: string) => boolean
	hasRole: (role: UserRole) => boolean
	updateUserPreferences: (preferences: Partial<UserPreferences>) => Promise<void>
	// Internal
	_initialize: () => Promise<void>
}

// Hook return type
export interface UseAuthReturn extends AuthState, AuthActions {}

// Keep ExtendedUser as a type alias for backward compatibility
export type ExtendedUser = AppUser

const IS_LOCAL_MODE = import.meta.env.VITE_LOCAL_MODE === "true"

/**
 * Map module-auth AuthUser -> contractacio AppUser (D-08).
 * AuthUser fields: userId, email, displayName
 */
function mapToAppUser(authUser: AuthUser): AppUser {
	return {
		uid: authUser.userId,
		email: authUser.email,
		displayName: authUser.displayName,
		emailVerified: true,
	}
}

/**
 * Zustand store for extended state only (not auth core).
 * Auth state comes from module-auth's React context.
 */
interface ExtrasState {
	reportsUser: ReportsUser | null
	organization: Organization | null
	permissions: string[]
	loading: boolean
	error: AuthError | null
}

const useExtrasStore = create<ExtrasState>(() => ({
	reportsUser: null,
	organization: null,
	permissions: [],
	loading: false,
	error: null,
}))

// Track loading to prevent duplicate fetches
let extrasLoadPromise: Promise<void> | null = null
let extrasLoaded = false

/**
 * Load reports-specific data via API.
 * Uses simpleGet which reads Bearer tokens from auth-bridge.
 */
async function loadExtras(): Promise<void> {
	// Prevent duplicate concurrent fetches
	if (extrasLoadPromise) return extrasLoadPromise

	extrasLoadPromise = (async () => {
		useExtrasStore.setState({ loading: true })

		// Retry transient failures. Right after login the module-auth access
		// token may not be ready for the very first /api/users/me call (it
		// rejects with a 401), and the backend identity auto-provisioning can
		// still be settling. Without this, a single failure left the store
		// with empty permissions until the user manually refreshed the page.
		const maxAttempts = 3
		try {
			for (let attempt = 1; attempt <= maxAttempts; attempt++) {
				try {
					const meData = await simpleGet<{
						reportsUser: ReportsUser | null
						organization: Organization | null
						permissions: string[]
					}>("/api/users/me")

					const { reportsUser, organization, permissions } = meData

					if (reportsUser) {
						useExtrasStore.setState({
							reportsUser,
							organization: organization || null,
							permissions: permissions || [],
							loading: false,
							error: null,
						})
					} else {
						useExtrasStore.setState({
							reportsUser: null,
							organization: null,
							permissions: [],
							loading: false,
							error: {
								code: "auth/user-not-in-database",
								message: "User not found in database",
								userFriendlyMessage: getAuthCopy().userNotRegistered,
							},
						})
					}
					// Terminal outcome (success or genuine not-registered) —
					// don't refetch on later remounts.
					extrasLoaded = true
					return
				} catch (error) {
					const errorMessage = error instanceof Error ? error.message : "Unknown error"
					const isModuleNotLicensed = errorMessage === "MODULE_NOT_LICENSED"
					const copy = getAuthCopy()

					if (isModuleNotLicensed) {
						// Terminal — licensing won't change by retrying.
						useExtrasStore.setState({
							loading: false,
							error: {
								code: "auth/module-not-licensed",
								message: errorMessage,
								userFriendlyMessage: copy.moduleNotLicensed,
							},
						})
						extrasLoaded = true
						return
					}

					console.error(`[AUTH] Error loading user data (attempt ${attempt}/${maxAttempts}):`, error)

					if (attempt < maxAttempts) {
						await new Promise((resolve) => setTimeout(resolve, 350 * attempt))
						continue
					}

					// Exhausted retries on a transient error: surface it but leave
					// extrasLoaded=false so a later remount / refreshUser retries
					// instead of latching empty permissions until a manual refresh.
					useExtrasStore.setState({
						loading: false,
						error: {
							code: "auth/refresh-error",
							message: errorMessage,
							userFriendlyMessage: copy.refreshError,
						},
					})
				}
			}
		} finally {
			extrasLoadPromise = null
		}
	})()

	return extrasLoadPromise
}

/**
 * useAuth hook -- adapter that delegates to @optimgov/module-auth
 * while preserving the same API surface for all 20+ consumers.
 */
export function useAuth(): UseAuthReturn {
	const moduleAuth = useModuleAuth()
	const extras = useExtrasStore()
	const bridgeSetRef = useRef(false)

	// ── Local mode: derive auth state from localStorage ──
	const localRole = IS_LOCAL_MODE
		? (typeof window !== "undefined" ? localStorage.getItem("local_role") : null)
		: null
	const isLocalAuthenticated = IS_LOCAL_MODE && !!localRole

	// Determine effective auth state
	const effectivelyAuthenticated = IS_LOCAL_MODE ? isLocalAuthenticated : moduleAuth.isAuthenticated

	const effectiveUser: AppUser | null = IS_LOCAL_MODE
		? (localRole
			? {
				uid: `local-${localRole}`,
				email: `local-${localRole}@dev.local`,
				displayName: `Local ${localRole.charAt(0).toUpperCase() + localRole.slice(1)}`,
				emailVerified: true,
			}
			: null)
		: (moduleAuth.user ? mapToAppUser(moduleAuth.user) : null)

	// Set auth bridge synchronously during render so child components'
	// effects can use it immediately. useEffect fires bottom-up (children
	// first), so deferring this to an effect causes 401s in child pages
	// that fetch in their own useEffect (e.g. TenderAssistantPage).
	// This is safe: idempotent write to a module-level variable, no React
	// state mutation. Bridge is cleared explicitly in the logout action.
	if (effectivelyAuthenticated) {
		if (IS_LOCAL_MODE && localRole) {
			setAuthBridge(async () => `local-${localRole}`)
		} else if (moduleAuth.isAuthenticated && moduleAuth.getAccessToken) {
			setAuthBridge(moduleAuth.getAccessToken)
		}
		bridgeSetRef.current = true
	}

	// Load extras (reports user, org, permissions) once after auth is ready.
	// extrasLoaded is module-level to survive mount/unmount cycles caused by
	// ReportsProtectedRoute toggling on loading state.
	// loadExtras() sets extrasLoaded=true itself once it reaches a terminal
	// outcome — marking it here (before the fetch resolved) was the bug that
	// permanently latched empty permissions when the first fetch failed.
	useEffect(() => {
		if (effectivelyAuthenticated && !extrasLoaded) {
			loadExtras()
		}
	}, [effectivelyAuthenticated])

	return {
		// State
		user: effectiveUser,
		loading: (IS_LOCAL_MODE ? false : moduleAuth.isLoading) || extras.loading,
		error: extras.error,
		isAuthenticated: effectivelyAuthenticated,
		isEmailVerified: true,
		initialized: IS_LOCAL_MODE ? true : !moduleAuth.isLoading,
		reportsUser: extras.reportsUser,
		organization: extras.organization,
		permissions: extras.permissions,
		authProvider: effectivelyAuthenticated ? "azure" : null,

		// Actions
		logout: async () => {
			try {
				storeManager.setLogoutInProgress(true)
				await storeManager.saveAllPendingData()
				storeManager.clearUserDataStores()
				setAuthBridge(null)
				useExtrasStore.setState({
					reportsUser: null,
					organization: null,
					permissions: [],
					loading: false,
					error: null,
				})
				extrasLoadPromise = null
				extrasLoaded = false
				storeManager.setLogoutInProgress(false)
			} catch (error) {
				console.warn("[AUTH] Error during store cleanup:", error)
				storeManager.setLogoutInProgress(false)
			}
			if (IS_LOCAL_MODE) {
				localStorage.removeItem("local_role")
				window.location.href = "/login"
			} else {
				await moduleAuth.logout()
			}
		},
		refreshUser: loadExtras,
		clearError: () => useExtrasStore.setState({ error: null }),
		checkPermission: (p: string) => extras.permissions.includes(p),
		hasRole: (r: UserRole) => extras.reportsUser?.role === r || false,
		updateUserPreferences: async (preferences: Partial<UserPreferences>): Promise<void> => {
			const { reportsUser } = useExtrasStore.getState()
			if (!reportsUser) return

			const updatedPreferences = {
				locale: "es",
				theme: "light" as const,
				notifications: {
					email: true,
					browser: true,
					reports: true,
					system: false,
				},
				...reportsUser.preferences,
				...preferences,
			}

			const updatedUser: ReportsUser = {
				...reportsUser,
				preferences: updatedPreferences,
			}

			useExtrasStore.setState({ reportsUser: updatedUser })

			try {
				await simplePatch("/api/users/me", { preferences: updatedPreferences }, { wafSafe: true })
				console.log("[AUTH] User preferences updated via API")
			} catch (error) {
				console.error("[AUTH] Failed to update user preferences:", error)
				useExtrasStore.setState({ reportsUser })
				throw error
			}
		},
		_initialize: async () => {
			// No-op -- module-auth handles initialization
		},
	}
}

/**
 * Hook for checking if user has specific role
 */
export function useRole(role: string): boolean {
	const { reportsUser } = useAuth()
	return reportsUser?.role === role || false
}

/**
 * Hook for checking if user has specific permission
 */
export function usePermission(permission: string): boolean {
	const { permissions } = useAuth()
	return permissions.includes(permission)
}

/**
 * Hook for requiring authentication (redirects if not authenticated)
 */
export function useRequireAuth(redirectTo: string = "/login") {
	const auth = useAuth()

	useEffect(() => {
		if (!auth.loading && !auth.isAuthenticated && auth.initialized) {
			console.log("[REQUIRE_AUTH] Redirecting to login")
			if (typeof window !== "undefined") {
				const currentPath = window.location.pathname
				const redirectUrl = `${redirectTo}?returnUrl=${encodeURIComponent(currentPath)}`
				window.location.href = redirectUrl
			}
		}
	}, [auth.loading, auth.isAuthenticated, auth.initialized, redirectTo])

	return auth
}
